/**
 * "Who do I ask about this plan?" (RP-34.1) — the account team, behind one header fact.
 *
 * The Author and the Account's owners link to email. The CRM's CSM and Client Partner are
 * synced from NOS as names only, so they stay plain text until NOS sends person ids; the
 * Account Management Pod does not exist yet (D18), so it is not listed. Fetched only when
 * opened.
 */

import { Mail } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { usePlanAccountTeam } from "@/hooks/use-plan-concerns";
import type { PlanAccountTeamMember } from "@/schemas/plan-concerns";

function MemberRow({ teamRole, member }: { teamRole: string; member: PlanAccountTeamMember }) {
  return (
    <li className="flex items-center justify-between gap-3">
      <span className="min-w-0">
        <span className="block truncate text-sm text-foreground">{member.name}</span>
        <span className="block text-xs text-muted-foreground">{teamRole}</span>
      </span>
      <a
        href={`mailto:${member.email}`}
        className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-primary hover:underline"
        title={member.email}
      >
        <Mail className="h-3.5 w-3.5" aria-hidden />
        Email
      </a>
    </li>
  );
}

function CrmRow({ teamRole, name }: { teamRole: string; name: string | null }) {
  return (
    <li className="flex items-center justify-between gap-3">
      <span className="min-w-0">
        <span className="block truncate text-sm text-foreground">
          {name ?? <span className="text-muted-foreground">Not assigned</span>}
        </span>
        <span className="block text-xs text-muted-foreground">{teamRole} · synced from CRM</span>
      </span>
    </li>
  );
}

/**
 * The popover trigger is whatever the caller renders as `children` — on the plan header
 * that is the Team line itself (`<Author> · Author · <CP> · CP`), so the names are the
 * button and no second "Account team" label is needed.
 */
export function PlanAccountTeamButton({
  planId,
  children,
}: {
  planId: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const { data, isLoading, isError, refetch } = usePlanAccountTeam(planId, open);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex min-w-0 items-center gap-1 text-left text-sm font-medium text-foreground underline decoration-dotted underline-offset-4 transition-colors hover:text-primary"
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80">
        {isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        )}
        {isError && (
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>Could not load the account team.</p>
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={() => refetch()}
            >
              Retry
            </button>
          </div>
        )}
        {data !== undefined && (
          <ul className="space-y-3">
            {data.author !== null && <MemberRow teamRole="Author" member={data.author} />}
            {data.accountOwners.map((owner) => (
              <MemberRow key={owner.email} teamRole="Account Owner" member={owner} />
            ))}
            {data.author === null && data.accountOwners.length === 0 && (
              <li className="text-sm text-muted-foreground">
                No Author or Account Owner is set for this plan.
              </li>
            )}
            <CrmRow teamRole="Client Success Manager" name={data.crmCsmName} />
            <CrmRow teamRole="Client Partner" name={data.crmClientPartnerName} />
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
